import { AlertTriangle, Code2, Copy, FileText, Film, Image as ImageIcon, ImageOff, Loader2, Music, Shapes, Type, type LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { Media, MediaKind } from '@/types/api'
import { formatDuration, hasTimeline, isContainKind, kindLabel, mediaFailureText, mediaStatusDisplay } from '../lib/display'

export const KIND_ICONS: Record<MediaKind, LucideIcon> = {
  image: ImageIcon,
  video: Film,
  audio: Music,
  svg: Shapes,
  pdf: FileText,
  html_package: Code2,
  font: Type,
}

/** Per-kind accent (icon tint + soft background), harmonised with the brand palette. */
export const KIND_ACCENT: Record<MediaKind, string> = {
  image: 'text-primary bg-primary/10',
  video: 'text-[oklch(0.6_0.19_330)] bg-[oklch(0.6_0.19_330/0.12)] dark:text-[oklch(0.75_0.15_330)]',
  audio: 'text-[oklch(0.62_0.15_160)] bg-[oklch(0.62_0.15_160/0.12)] dark:text-success',
  svg: 'text-[oklch(0.66_0.15_55)] bg-[oklch(0.7_0.15_60/0.14)] dark:text-warning',
  pdf: 'text-danger bg-danger/10',
  html_package: 'text-info bg-info/12',
  font: 'text-brand-2 bg-brand-2/12',
}

export function KindIcon({ kind, className }: { kind: MediaKind; className?: string }) {
  const Icon = KIND_ICONS[kind] ?? FileText
  return <Icon className={className} aria-hidden />
}

export function KindTile({ kind, size = 'md', className }: { kind: MediaKind; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-lg',
        KIND_ACCENT[kind],
        size === 'sm' ? 'size-7 [&_svg]:size-3.5' : size === 'lg' ? 'size-14 rounded-2xl [&_svg]:size-7' : 'size-9 [&_svg]:size-4',
        className,
      )}
    >
      <KindIcon kind={kind} />
    </span>
  )
}

/** Small glassy kind chip shown over thumbnails. */
export function KindChip({ kind, className }: { kind: MediaKind; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[10.5px] font-semibold tracking-wide text-white uppercase backdrop-blur-sm', className)}>
      <KindIcon kind={kind} className="size-3" />
      {kindLabel(kind)}
    </span>
  )
}

export function DurationChip({ ms, className }: { ms: number; className?: string }) {
  return <span className={cn('rounded-md bg-black/60 px-1.5 py-0.5 text-[11px] font-medium text-white tabular backdrop-blur-sm', className)}>{formatDuration(ms)}</span>
}

const CHECKER =
  'bg-[conic-gradient(var(--color-muted)_0_25%,transparent_0_50%,var(--color-muted)_0_75%,transparent_0)] bg-[length:16px_16px] bg-card'

/**
 * Thumbnail with graceful fallbacks: kind tile until processed / when the signed URL
 * fails. SVGs are only ever rendered through `<img>` (never inline markup).
 */
export function MediaThumb({ media, className, iconSize = 'md', fit }: { media: Media; className?: string; iconSize?: 'sm' | 'md' | 'lg'; fit?: 'cover' | 'contain' }) {
  const [broken, setBroken] = useState<string | null>(null)
  const src = media.thumbnail_url
  const contain = fit ? fit === 'contain' : isContainKind(media.kind)
  if (!src || broken === src) {
    return (
      <div className={cn('flex items-center justify-center bg-muted/60', className)}>
        {src && broken === src ? <ImageOff className="size-5 text-muted-foreground" aria-hidden /> : <KindTile kind={media.kind} size={iconSize} />}
      </div>
    )
  }
  return (
    <div className={cn('overflow-hidden', contain ? CHECKER : 'bg-muted', className)}>
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={() => setBroken(src)}
        className={cn('size-full', contain ? 'object-contain p-2' : 'object-cover')}
      />
    </div>
  )
}

export function MediaStatusBadge({ media, className }: { media: Pick<Media, 'status' | 'failure_code' | 'failure_detail'>; className?: string }) {
  const s = mediaStatusDisplay(media.status)
  if (media.status === 'ready') return null
  const variant = s.tone === 'danger' ? 'danger' : s.tone === 'warning' ? 'warning' : s.tone === 'info' ? 'default' : 'secondary'
  return (
    <Badge variant={variant} className={className} title={mediaFailureText(media) ?? undefined}>
      {s.busy ? <Loader2 className="animate-spin" /> : media.failure_code === 'duplicate' ? <Copy /> : <AlertTriangle />}
      {s.label}
    </Badge>
  )
}

/** Overlay for non-ready media on a thumbnail: shimmer while busy, error card when broken. */
export function StatusOverlay({ media, compact = false }: { media: Media; compact?: boolean }) {
  const s = mediaStatusDisplay(media.status)
  if (media.status === 'ready') return null
  if (s.busy) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-background/55 backdrop-blur-[1px]">
        <div className="absolute inset-0 animate-shimmer bg-[linear-gradient(90deg,transparent_0%,color-mix(in_oklch,var(--primary)_14%,transparent)_50%,transparent_100%)] bg-[length:800px_100%] bg-no-repeat" aria-hidden />
        <Loader2 className="relative size-5 animate-spin text-primary" aria-hidden />
        {!compact && <span className="relative text-[11.5px] font-medium text-foreground/80">{s.label}…</span>}
      </div>
    )
  }
  const text = mediaFailureText(media)
  return (
    <div className={cn('absolute inset-0 flex flex-col items-center justify-center gap-1 p-3 text-center', s.tone === 'danger' ? 'bg-danger/12' : 'bg-warning/14', 'backdrop-blur-[1px]')}>
      <span className={cn('flex size-8 items-center justify-center rounded-full bg-card shadow-sm', s.tone === 'danger' ? 'text-danger' : 'text-[color-mix(in_oklch,var(--warning),black_35%)] dark:text-warning')}>
        {media.failure_code === 'duplicate' ? <Copy className="size-4" /> : <AlertTriangle className="size-4" />}
      </span>
      {!compact && (
        <>
          <span className="text-[11.5px] font-semibold">{s.label}</span>
          <span className="line-clamp-2 text-[11px] text-muted-foreground">{text}</span>
        </>
      )}
    </div>
  )
}

export function TimelineChip({ media, className }: { media: Media; className?: string }) {
  if (!hasTimeline(media.kind) || !media.duration_ms) return null
  return <DurationChip ms={media.duration_ms} className={className} />
}
